import { useQuery } from "@tanstack/react-query";
import type React from "react";

import { Modal, type ModalBaseProps } from "@/components/Modal/Modal";
import { type RatableItem, RatableSuggestionsList } from "@/components/RatableSuggestionsList/RatableSuggestionsList";
import { Button } from "@/design-system/Button";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Stack } from "@/design-system/Stack";
import { surveyMutations } from "@/queries/surveys/mutations";
import { useSurveyQueries } from "@/queries/surveys/queryOptions";
import { useTranslation } from "@/translations";

type SurveyFollowUpModalProps = ModalBaseProps & {
  surveyId: string;
};

export function SurveyFollowUpTipsModal({
  surveyId,
  isOpened,
  onOpenChange,
}: SurveyFollowUpModalProps): React.ReactNode {
  const { t } = useTranslation();

  const queries = useSurveyQueries();
  const {
    data: surveyFollowUps,
    isPending,
    error,
  } = useQuery({
    ...queries.suggestedFollowUps(surveyId),
    enabled: isOpened,
  });

  const rateMutation = surveyMutations.useRateSurveyFollowUp();
  const handleRateFollowUp = (followUpId: string, ratingValue: "positive" | "negative") => {
    rateMutation.mutate({
      surveyId,
      followUpId,
      rating: ratingValue,
    });
  };

  const ratableItems: RatableItem[] =
    surveyFollowUps?.map((suggestion) => ({
      id: suggestion.id,
      title: suggestion.suggestion,
      details: [suggestion.rationale],
      rating: suggestion.rating,
      ratedAt: suggestion.ratedAt,
    })) || [];

  return (
    <Modal.Root
      size="md"
      title={t("page.survey-details.follow-up-modal.title")}
      description={t("page.survey-details.follow-up-modal.description")}
      {...{ isOpened, onOpenChange }}
    >
      <Modal.Body>
        {isPending && <FullSizeLoader withPadding />}

        {error && (
          <Stack gap="md" align="center">
            <p className="text-body text-text-danger-secondary">{t("page.survey-details.follow-up-modal.error")}</p>
          </Stack>
        )}

        {surveyFollowUps && ratableItems.length > 0 && !isPending && !error && (
          <Stack gap="sm">
            <h4 className="text-heading-4">{t("page.survey-details.follow-up-modal.suggestions-title")}</h4>
            <RatableSuggestionsList items={ratableItems} onRateItem={handleRateFollowUp} />
          </Stack>
        )}
      </Modal.Body>

      <Modal.Actions>
        <Modal.Close>
          <Button>{t("common.action.close")}</Button>
        </Modal.Close>
      </Modal.Actions>
    </Modal.Root>
  );
}
